import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  calmPath,
  communityPath,
  culturePath,
  foodPath,
  eventsPath,
  guidesPath,
  guides2Path,
  MOSTRAR_GUIAS,
  publicationDetailPath,
} from "../../../routes/routes";
import { LangContext } from "../../../contexts/Contexts";
import { SidebarSection } from "./components/SidebarSection";
import "./styles/newNavigationSideBar.css";
import {
  RadarChartOutlined,
  SketchOutlined,
  SwitcherFilled,
  BankFilled,
  CrownFilled,
  FrownTwoTone,
  TrophyOutlined,
  CompassOutlined
} from "@ant-design/icons";
import { SidebarItem } from "./components/SidebarItem";

const SIDEBAR_ACTIVE_ROUTE_KEY = "vnSidebarActiveRoute";

const tOr = (uiTexts, key, fb, lang = "en") => {
  if (typeof fb === "object") {
    const value = uiTexts?.[key];
    if (value && lang === "es" && value === fb.en) return fb.es;
    if (value && lang === "en" && value === fb.es) return fb.en;
    if (value) return value;
    return fb[lang] || fb.es || fb.en || "";
  }
  if (uiTexts?.[key]) return uiTexts[key];
  return fb;
};

const Item = ({ route, icon, label, active, muted, onClick }) => (
  <Link
    to={route}
    onClick={onClick}
    className={[
      "vn-item",
      active ? "is-active" : "",
      muted ? "is-muted" : "",
      "vn-item-explore",
    ].join(" ")}
  >
    <span className="vn-item-icon">
      
      <div>{icon}</div>
    </span>
    <span className="vn-item-label">{label}</span>
  </Link>
);

const NewNavigationsButtons = ({ uiTexts }) => {
  const { pathname } = useLocation();
  const { lang } = useContext(LangContext);
  const currentLang = (lang || "en").toLowerCase();
  const [storedActiveRoute, setStoredActiveRoute] = useState(() =>
    localStorage.getItem(SIDEBAR_ACTIVE_ROUTE_KEY),
  );

  const foros = useMemo(
    () => [
      {
        icon: <RadarChartOutlined />,
        label: tOr(uiTexts, "LB0059", { es: "Calma", en: "Calm" }, currentLang),
        route: calmPath,
      },
      {
        icon: <SketchOutlined />,
        label: tOr(uiTexts, "LB0056", { es: "Cultura", en: "Culture" }, currentLang),
        route: culturePath,
      },
      {
        icon: <BankFilled />,
        label: tOr(uiTexts, "LB0058", { es: "Comida", en: "Food" }, currentLang),
        route: foodPath,
      },
      {
        icon: <CrownFilled />,
        label: tOr(uiTexts, "LB0057", { es: "Comunidad", en: "Community" }, currentLang),
        route: communityPath,
      },
    ],
    [currentLang, uiTexts, calmPath, culturePath, foodPath, communityPath]
  );

 const explora = useMemo(
    () => [
      {
        key: "events",
        icon: <TrophyOutlined />,
        label: tOr(uiTexts, "LB0167", { es: "Eventos", en: "Events" }, currentLang),
        route: eventsPath,
      },
      {
        key: "guides",
        // Guias V1 oculta del menu: el catalogo vigente es Guides 2.0 (/guides2).
        // Se oculta con `show` en vez de borrar la entrada porque la ruta /guides
        // sigue registrada en AppRouter y sirve por enlace directo.
        show: false,
        icon: <CompassOutlined />,
        // TODO: mover a clave del esquema remoto (LBxxxx) cuando exista.
        label: currentLang === "es" ? "Guías" : "Guides",
        route: guidesPath,
      },
      {
        key: "guides2",
        // Misma brujula que llevaba Guias V1: al ser el unico catalogo de
        // guias del menu, se presenta simplemente como "Guias", sin version.
        // Solo en ambientes con REACT_APP_MOSTRAR_GUIAS=true (Pre y Demo);
        // en Produccion no se define y la entrada no aparece.
        show: MOSTRAR_GUIAS,
        icon: <CompassOutlined />,
        // TODO: mover a clave del esquema remoto (LBxxxx) cuando exista.
        label: currentLang === "es" ? "Guías" : "Guides",
        route: guides2Path,
      },
      // {
      //   key: "housing",
      //   icon: "/img/icons/co-living.svg",
      //   label: tOr(uiTexts, "LB0055", "Housing"),
      //   route: housingPropertiesPath,
      // },
    ],
    [currentLang, uiTexts]
  );

  const exploraVisible = useMemo(
    () => explora.filter((it) => it.show !== false),
    [explora]
  );

  const navRoutes = useMemo(
    () => [...foros, ...exploraVisible],
    [foros, exploraVisible],
  );

  const isDetailRoute =
    pathname === publicationDetailPath ||
    pathname.startsWith("/forum/publication");

  const exploreSectionTitle = tOr(
    uiTexts,
    "LB1015",
    { es: "Explora", en: "Explore" },
    currentLang,
  );

  const isCurrentRoute = (route) =>
    pathname === route || pathname.startsWith(`${route}/`);

  useEffect(() => {
    const matchedRoute = navRoutes.find((item) => isCurrentRoute(item.route))?.route;

    if (matchedRoute) {
      localStorage.setItem(SIDEBAR_ACTIVE_ROUTE_KEY, matchedRoute);
      setStoredActiveRoute(matchedRoute);
    }
  }, [navRoutes, pathname]);

  const rememberActiveRoute = (route) => {
    localStorage.setItem(SIDEBAR_ACTIVE_ROUTE_KEY, route);
    setStoredActiveRoute(route);
  };

  const isActive = (route) =>
    isCurrentRoute(route) || (isDetailRoute && storedActiveRoute === route);

  return (
    <>
      <SidebarSection
        title={tOr(uiTexts, "LB1014", { es: "Foros", en: "Forums" }, currentLang)}
        defaultCollapsed
        storageKey="vn-sidebar-section-forums-collapsed"
      >
        {foros.map((f) => (
          <Item
            key={f.label}
            route={f.route}
            icon={f.icon}
            label={f.label}
            active={isActive(f.route)}
            onClick={() => rememberActiveRoute(f.route)}
            muted
          />
        ))}
      </SidebarSection>

      <SidebarSection
        title={exploreSectionTitle}
        defaultCollapsed
        storageKey="vn-sidebar-section-explore-collapsed"
      >
        {exploraVisible.map((item) => (
         /*  <Item
            key={e.label}
            route={e.route}
            icon={e.icon}
            label={e.label}
            active={isActive(e.route)}
            onClick={() => rememberActiveRoute(e.route)}
          /> */

          <SidebarItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              to={item.route}
              active={isActive(item.route)}
              onClick={() => rememberActiveRoute(item.route)}
              muted
            />
        ))}
      </SidebarSection>
    </>
  );
};

export default NewNavigationsButtons;
